/**
 * The M2 bridge's scan (M3 plan §7 item 7, slice R22j; spec §6.2, §6.3 step 1).
 *
 * - **Targeted relay scan:** M2's targeted R scan (R or pad X held `mining.json scan.holdSeconds` on the target,
 *   within the fitted scanner's range to its surface) extended to relays. M2's nav targets have no relay kind, so the
 *   relay's target is the Reach nav target (the system map's cruise target, or the route's next relay). An offline,
 *   unscanned relay of the current system scans in normal flight: its state is marked `scanned` and
 *   `REV.relayScanned` fires, so the HUD toasts it and the relay panel shows its repair list (delivered/required).
 *   Range is measured to the ring's inner edge (`apertureM`), as M2 measures a rock to its surface.
 * - **Deep-scan target rule** (`deepScanRequiresNoTarget`): R is M2's targeted scan whenever a target is selected, so
 *   `scanTarget` reports M1's combat target, a nav target M2 would scan (a rock, fragment or wreck outside Combat
 *   mode), or a relay target this scan would take. The exploration scanner asks it before charging a deep scan.
 * - **Link:** inert until `link` gets the `m2` service (the `bridgeScan` feature does it at boot), so M3 alone and
 *   the title screen behave as before.
 *
 * `step` and `scanTarget` allocate nothing (D-014); a scan completing is a rare event.
 */
import { SIM_DT } from '../../core/loop'
import type { RelaySite, SystemLayout } from '../../galaxy/layout/types'
import { emitReach, REV } from '../../reach/events'
import type { ReachCore, ReachSubsystem } from '../../reach/runtime/types'
import { ensureRelayState } from '../../reach/state'
import type { NavTargetRef } from '../../reach/types'
import type { Sim } from '../../sim'
import { NAV, TOOL, type M2Runtime } from '../../space/contracts'
import { Life, Pos64, Target } from '../../world/components'

/** Before exploration (300), so a relay scanned this tick no longer counts as a target for its deep scan. */
export const BRIDGE_SCAN_ORDER = 290
export const BRIDGE_SCAN_NAME = 'bridgeScan'

/** Progress at or above this counts as a full hold (60 ticks of 1/60 s sum to 1 only within rounding). */
const SCAN_DONE = 1 - 1e-9

/** What the scan reads from M2. Every method is allocation-free. */
export interface ScanLink {
  /** M2 would run its targeted scan on its nav target (a rock, fragment or wreck, outside Combat mode). */
  toolTarget(): boolean
  /** The fitted scanner's range in metres (M2's career mirror); 0 = no scanner (no relay scan). */
  scannerRangeM(): number
  /** M2's targeted-scan hold in seconds (`mining.json scan.holdSeconds`). */
  holdSeconds(): number
}

/** The relay scan's readout (for a HUD): the relay site index under the scan (−1 = none) and its 0..1 progress. */
export interface RelayScanReadout {
  relayIndex: number
  progress: number
}

export interface BridgeScanSubsystem extends ReachSubsystem {
  readonly readout: RelayScanReadout
  /** Binds (or with null, unbinds) M2. */
  link(source: ScanLink | null): void
  /** True while a target is selected, so R is a targeted scan rather than the deep scan. Allocation-free. */
  scanTarget(core: ReachCore, sim: Sim): boolean
}

/** A `ScanLink` over the `m2` service, looked up on every call (it appears once the career feature has run). */
export function m2ScanLink(m2: () => M2Runtime | undefined): ScanLink {
  return {
    toolTarget(): boolean {
      const space = m2()?.space
      if (space === undefined) return false
      const player = space.player
      const kind = player.nav.kind
      return player.mode !== TOOL.combat && (kind === NAV.rock || kind === NAV.fragment || kind === NAV.wreck)
    },
    scannerRangeM: () => m2()?.space.mirror.tools.scannerRange ?? 0,
    holdSeconds: () => m2()?.data.mining.scan.holdSeconds ?? 1,
  }
}

/** The bridge scan among a runtime's subsystems, or null (not discovered, e.g. a test core). */
export function findBridgeScan(list: readonly ReachSubsystem[]): BridgeScanSubsystem | null {
  for (let i = 0; i < list.length; i++) {
    const s = list[i] as Partial<BridgeScanSubsystem>
    if (s.name === BRIDGE_SCAN_NAME && typeof s.link === 'function' && typeof s.scanTarget === 'function') {
      return s as BridgeScanSubsystem
    }
  }
  return null
}

interface ScanState {
  link: ScanLink | null
  readout: RelayScanReadout
  /** Relay-target lookup cache: the nav ref and layout it was resolved for. */
  ref: NavTargetRef | null
  layout: SystemLayout | null
  site: RelaySite | null
  /** The current scan completed during this hold (no repeat until R is released). */
  latched: boolean
}

function relayOf(st: ScanState, core: ReachCore): RelaySite | null {
  const ref = core.nav.target
  const layout = core.session.layout
  if (ref === st.ref && layout === st.layout) return st.site
  st.ref = ref
  st.layout = layout
  st.site = null
  if (ref === null || layout === null || ref.kind !== 'relay') return null
  const relays = layout.relays
  for (let i = 0; i < relays.length; i++) {
    if (relays[i].relayId === ref.id) {
      st.site = relays[i]
      break
    }
  }
  return st.site
}

/** The targeted relay: the nav target's relay when it is offline, unscanned and within scanner range. */
function relayInReach(st: ScanState, core: ReachCore, sim: Sim): RelaySite | null {
  const link = st.link
  if (link === null || !core.session.ready || core.travel.mode !== 'normal') return null
  const p = sim.playerEid
  if (p < 0 || Life.alive[p] !== 1) return null
  const site = relayOf(st, core)
  if (site === null) return null
  const rs = core.state.relays[site.relayId]
  if (rs?.scanned === true) return null
  const status = rs !== undefined ? rs.status : core.galaxy.relays[site.index]?.online === true ? 'online' : 'offline'
  if (status !== 'offline') return null
  const range = link.scannerRangeM()
  if (!(range > 0)) return null
  const dx = site.position[0] - Pos64.x[p]
  const dy = site.position[1] - Pos64.y[p]
  const dz = site.position[2] - Pos64.z[p]
  return Math.sqrt(dx * dx + dy * dy + dz * dz) - site.apertureM <= range ? site : null
}

function resetReadout(st: ScanState): void {
  st.readout.relayIndex = -1
  st.readout.progress = 0
}

function stepRelayScan(st: ScanState, core: ReachCore, sim: Sim): void {
  if (!core.input.scanHeld) {
    st.latched = false
    resetReadout(st)
    return
  }
  const site = relayInReach(st, core, sim)
  if (site === null || st.latched || st.link === null) {
    resetReadout(st)
    return
  }
  const r = st.readout
  if (r.relayIndex !== site.index) {
    r.relayIndex = site.index
    r.progress = 0
  }
  const hold = st.link.holdSeconds()
  r.progress += hold > 0 ? SIM_DT / hold : 1
  if (r.progress < SCAN_DONE) return
  ensureRelayState(core.state, core.galaxy, site.relayId).scanned = true
  emitReach(core.events, REV.relayScanned, site.index, 0, site.position[0], site.position[1], site.position[2], 0)
  st.latched = true
  resetReadout(st)
}

export function createBridgeScan(): BridgeScanSubsystem {
  const st: ScanState = {
    link: null,
    readout: { relayIndex: -1, progress: 0 },
    ref: null,
    layout: null,
    site: null,
    latched: false,
  }
  return {
    name: BRIDGE_SCAN_NAME,
    order: BRIDGE_SCAN_ORDER,
    readout: st.readout,
    init(): void {
      resetReadout(st)
    },
    step(core: ReachCore, sim: Sim): void {
      if (st.link === null) return
      stepRelayScan(st, core, sim)
    },
    onSystemLeave(): void {
      st.latched = false
      resetReadout(st)
    },
    link(source: ScanLink | null): void {
      st.link = source
      st.latched = false
      resetReadout(st)
    },
    scanTarget(core: ReachCore, sim: Sim): boolean {
      const p = sim.playerEid
      if (p >= 0 && Target.eid[p] >= 0) return true
      const link = st.link
      if (link === null) return false
      return link.toolTarget() || relayInReach(st, core, sim) !== null
    },
  }
}
